import { useEffect } from 'react';

type EnsureModalInteractionOptions = {
    element?: Element | null;
    selector?: string | string[];
    root?: ParentNode;
};

const REACT_ARIA_TOP_LAYER_ATTR = 'data-react-aria-top-layer';

const applyModalInteractionAttributes = (element: Element): void => {
    element.setAttribute(REACT_ARIA_TOP_LAYER_ATTR, 'true');
    element.removeAttribute('aria-hidden');
    element.removeAttribute('inert');
};

/**
 * Ensures a given element remains interactive when a React Aria modal is open.
 * This marks the element as a React Aria top-layer node and removes inert/aria-hidden.
 */
export const ensureModalInteraction = (options: EnsureModalInteractionOptions): (() => void) => {
    const { element, selector, root = document.body } = options;

    // If an element is directly provided, use that
    if (element) {
        applyModalInteractionAttributes(element);
        return () => undefined;
    }

    // If no selector is provided, do nothing
    if (!selector) {
        return () => undefined;
    }

    // Normalize selector to array
    const selectors = Array.isArray(selector) ? selector : [selector];

    // Resolve and apply attributes to all matching elements
    const resolveAndApply = (): void => {
        for (const sel of selectors) {
            const elements = root.querySelectorAll(sel);
            elements.forEach((el) => applyModalInteractionAttributes(el));
        }
    };

    resolveAndApply();

    // Set up a MutationObserver to watch for new matching elements being added to the DOM
    const observer = new MutationObserver(() => {
        resolveAndApply();
    });

    observer.observe(root, { childList: true, subtree: true });

    return () => observer.disconnect();
};

/**
 * React hook to ensure a given element remains interactive when a React Aria modal is open.
 * This marks the element as a React Aria top-layer node and removes inert/aria-hidden.
 */
export const useEnsureModalInteraction = (options: EnsureModalInteractionOptions): void => {
    useEffect(() => {
        const cleanup = ensureModalInteraction(options);
        return () => {
            cleanup();
        };
    }, [options.element, options.selector, options.root]);
};
